import React from 'react'
import Table from '../../shared/table'
import { StackView, Link } from '@planningcenter/tapestry-react'
import { PageHeading, Text, Code } from '../../shared/typography'

const ErrorCodesTable = () => {
  const columns = [
    {
      header: 'Error Code',
      cell: row => <Code>{row.code}</Code>,
    },
    {
      header: 'Description',
      cell: 'description',
    },
    {
      header: 'Example Cause',
      cell: row => row.cause(),
    },
  ]

  const data = [
    {
      code: 400,
      description: 'Bad Request',
      cause: () => <Text>Something is wrong in your request</Text>,
    },
    {
      code: 401,
      description: 'Unauthorized',
      cause: () => (
        <Text>You did not use the proper API token and/or secret.</Text>
      ),
    },
    {
      code: 403,
      description: 'Forbidden',
      cause: () => (
        <StackView axis="vertical" spacing={1}>
          <Text>You tried to view a resource you don’t have access to</Text>
          <Text>
            You tried to create a resource, but your user has an insufficient{' '}
            role.
          </Text>
        </StackView>
      ),
    },
    {
      code: 404,
      description: 'Not Found',
      cause: () => (
        <Text>
          You’ve tried to access a URL or resource ID that doesn’t exist.
        </Text>
      ),
    },
    {
      code: 409,
      description: 'Conflict',
      cause: () => (
        <StackView axis="vertical" spacing={1}>
          <Text>
            A conflict occurs when creating a resource encounters a uniqueness{' '}
            validation error.
          </Text>
          <Text>
            Normally uniqueness errors will be returned in a 422 Validation
            Error.
          </Text>
          <Text>
            There are some cases (a multi-threaded application) where multiple{' '}
            threads will send simultaneous requests to create the same resource.
          </Text>
          <Text>
            If you are encountering this error, please make sure that you're app
            is only sending a single create request for a resource.
          </Text>
          <Text>
            If there is only one request, please file a bug report by creating
            an issue on{' '}
            <Link to="https://github.com/planningcenter/developers/" external>
              Github
            </Link>
          </Text>
        </StackView>
      ),
    },
    {
      code: 422,
      description: 'Validation Error',
      cause: () => (
        <StackView axis="vertical" spacing={1}>
          <Text>The value you have passed for an attribute isn't valid.</Text>
          <Text>
            The response body will provide the attribute that is invalid.
          </Text>
          <Text>
            The response body will provide the attribute that is invalid.
          </Text>
          <Text>
            <Text as="span" inline italic>
              Note:
            </Text>{' '}
            There can be more than one invalid attribute.
          </Text>
        </StackView>
      ),
    },
    {
      code: 500,
      description: 'Internal Server Error',
      cause: () => (
        <Text>We had a problem with our server. Try again later.</Text>
      ),
    },
    {
      code: 503,
      description: 'Service Unavailable',
      cause: () => (
        <Text>
          We’re temporarily offline for maintenance. Please try again later.
        </Text>
      ),
    },
  ]

  return <Table columns={columns} data={data} />
}

const ErrorHintsTable = () => {
  const columns = [
    {
      header: 'Error Hint',
      cell: row => <Code>{row.hint}</Code>,
    },
    {
      header: 'Description',
      cell: row => row.description(),
    },
  ]

  const data = [
    // **
    /* These are from https://github.com/planningcenter/pco-api-engine/blob/main/lib/pco/api_engine/error_codes.rb */
    /*
    /* Some error codes in that file are intentionally not included here for security reasons. */

    {
      hint: 'BLACKBIRD',
      description: () => (
        <Text>
          The request includes an unsupported <Code>kind</Code> parameter.
        </Text>
      ),
    },
    {
      hint: 'CHINCHILLA',
      description: () => (
        <Text>The client application was not found or is invalid.</Text>
      ),
    },
    {
      hint: 'FLAMINGO',
      description: () => <Text>The authenticated user does not exist.</Text>,
    },
    {
      hint: 'LEMMING',
      description: () => (
        <Text>The authenticated organization does not exist.</Text>
      ),
    },
    {
      hint: 'PELICAN',
      description: () => (
        <Text>The provided OAuth token does not have a required scope.</Text>
      ),
    },
    {
      hint: 'BABOON',
      description: () => (
        <StackView spacing={1}>
          <Text>The provided OAuth access token is expired.</Text>
          <Text size={5} italic>
            Prior to March 2025 this also included revoked OAuth tokens.
          </Text>
        </StackView>
      ),
    },
    {
      hint: 'CAPUCHIN',
      description: () => (
        <Text>The provided OAuth access token has been revoked.</Text>
      ),
    },
    {
      hint: 'TRASH_PANDA',
      description: () => (
        <Text>
          The authenticated person does not have access to the requested
          application.
        </Text>
      ),
    },
    {
      hint: 'WEASEL',
      description: () => (
        <Text>The signature's scope is not valid for the requested path.</Text>
      ),
    },
    {
      hint: 'ANTEATER',
      description: () => (
        <Text>
          Signature validation failed with a missing authorization header.
        </Text>
      ),
    },
    {
      hint: 'BLUE_JAY',
      description: () => (
        <Text>
          Signature authentication failed because the authorization header is an
          incorrect format.
        </Text>
      ),
    },
    {
      hint: 'MOCKINGBIRD',
      description: () => (
        <Text>
          The signature authentication failed because of an invalid token.
        </Text>
      ),
    },
  ]

  return <Table columns={columns} data={data} />
}

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Errors</PageHeading>

      <Text>The Planning Center API uses the following error codes:</Text>

      <ErrorCodesTable />

      <Text>Additionally, your error code may include an error hint.</Text>

      <ErrorHintsTable />
    </StackView>
  )
}

export default page
